/**
 * F2.4b1 / F2.4b2a / F2.4b2b / F2.5a3 — helpers purs intention / choix / payNow / encaisser ticket.
 */

export const KEEP_POINTS_VALUE = '__KEEP_POINTS__';

export const REWARD_INVALIDATED_MESSAGE =
  'Cette récompense n’est plus disponible. Les points du client sont conservés.';

/** @deprecated message historique b2a — encaissement ticket désormais supporté (b2b) */
export const REWARD_TICKET_ENCAISSER_REQUIRES_B2B_MESSAGE =
  'L’application de la récompense sur un ticket en attente arrive en F2.4b2b. Mettez le ticket de côté, ou choisissez « Conserver les points » pour encaisser maintenant.';

/** @deprecated alias historique b1 */
export const REWARD_APPLY_REQUIRES_B2_MESSAGE = REWARD_TICKET_ENCAISSER_REQUIRES_B2B_MESSAGE;

/**
 * Ligne catalogue (affichage) — jamais de calcul local du montant € pour %.
 */
export function formatCaisseCatalogRewardOfferLine(reward) {
  const pts = Number(reward?.coutPoints);
  const ptsPart = Number.isFinite(pts) ? `${pts} pts` : 'pts';
  if (reward?.effectType === 'REDUCTION_POURCENT') {
    const p = Number(reward.valeurPourcent);
    if (Number.isFinite(p) && p > 0) {
      const label = Number.isInteger(p) ? String(p) : String(p);
      return `${ptsPart} → ${label} %`;
    }
    return `${ptsPart} → %`;
  }
  const euros = Number(reward?.valeurEuros);
  if (Number.isFinite(euros)) {
    return `${ptsPart} → ${euros.toFixed(2)} €`;
  }
  return ptsPart;
}

/**
 * Phrase post-quote : économie € serveur uniquement.
 */
export function formatCaisseQuoteSavingsLine(quote, reward) {
  const reduction = Number(quote?.reductionAvantage);
  if (!Number.isFinite(reduction) || !(reduction > 0)) return null;
  const eurosLabel = reduction.toFixed(2);
  if (reward?.effectType === 'REDUCTION_POURCENT') {
    const p = Number(reward.valeurPourcent);
    if (Number.isFinite(p) && p > 0) {
      const pctLabel = Number.isInteger(p) ? String(p) : String(p);
      return `${pctLabel} % de réduction — Vous économisez : ${eurosLabel} €`;
    }
  }
  return `Vous économisez : ${eurosLabel} €`;
}

/**
 * Message confirm — € toujours issu de la quote serveur (reductionAvantage / valeurOfferte).
 */
export function formatCaisseRewardConfirmMessage(quote, reward, clientLabel) {
  const points = Number(quote?.points);
  const euros =
    quote?.valeurOfferte != null
      ? Number(quote.valeurOfferte)
      : Number(quote?.reductionAvantage);
  const libelle = reward?.libelle ? String(reward.libelle) : 'cette récompense';
  const who = clientLabel ? ` pour ${clientLabel}` : '';
  if (!Number.isFinite(points) || points < 1 || !Number.isFinite(euros) || !(euros > 0)) {
    return `Utiliser les points${who} pour « ${libelle} » sur cette vente ?`;
  }
  const ptsLabel = points > 1 ? 'points' : 'point';
  const eurosLabel = Number.isInteger(euros) ? String(euros) : euros.toFixed(2);
  if (reward?.effectType === 'REDUCTION_POURCENT') {
    const p = Number(reward.valeurPourcent);
    if (Number.isFinite(p) && p > 0) {
      const pctLabel = Number.isInteger(p) ? String(p) : String(p);
      return `Utiliser ${points} ${ptsLabel}${who} pour ${pctLabel} % de réduction (${eurosLabel} €) sur cette vente ?`;
    }
  }
  return `Utiliser ${points} ${ptsLabel}${who} pour obtenir ${eurosLabel} € sur cette vente ?`;
}

/** Estimation total payable après quote (affichage uniquement — vérité = serveur). */
export function estimateCaissePayableTotal({
  sousTotal,
  reductionPromo = 0,
  reductionAvantage = 0,
}) {
  const base = Number(sousTotal) || 0;
  const promo = Number(reductionPromo) || 0;
  const reward = Number(reductionAvantage) || 0;
  return Math.max(0, Math.round((base - promo - reward) * 100) / 100);
}

export function buildTicketFideliteFields({ clientUserId, confirmedRewardId }) {
  if (!clientUserId) return {};
  const fields = { clientUserId };
  if (confirmedRewardId) {
    fields.rewardId = confirmedRewardId;
  }
  return fields;
}

/** Payload vente immédiate (payNow) — rewardId exact, pas de montants front */
export function buildImmediateVenteFideliteFields({ clientUserId, confirmedRewardId }) {
  if (!clientUserId) return {};
  const fields = { clientUserId };
  if (confirmedRewardId) {
    fields.utiliserAvantage = true;
    fields.rewardId = confirmedRewardId;
  }
  return fields;
}

/** Interdit : payload rewardId sans client */
export function assertTicketIntentInvariant({ clientUserId, rewardId }) {
  if (rewardId && !clientUserId) {
    throw new Error('REWARD_REQUIRES_CLIENT');
  }
  return true;
}

/**
 * F2.4b2b — payNow et encaissement ticket + reward autorisés.
 * Soft-skip interdit : ne jamais omettre un rewardId confirmé côté UI/API.
 */
export function resolveCaisseImmediatePayGate({
  confirmedRewardId: _confirmedRewardId,
  activeTicketId: _activeTicketId = null,
}) {
  return { action: 'allow', message: null };
}

/**
 * Gate paiement quand une récompense est confirmée.
 * Soft-skip interdit : pas d’encaissement silencieux sans reward.
 */
export function resolveCaisseConfirmedRewardPayGate({
  confirmedRewardId,
  quote,
  quoteLoading = false,
  quoteError = null,
}) {
  if (!confirmedRewardId) {
    return { action: 'allow', message: null };
  }
  if (quoteLoading) {
    return {
      action: 'block',
      message: 'Vérification de la récompense en cours…',
    };
  }
  if (quoteError || !quote || quote.applicable !== true) {
    return {
      action: 'block',
      message: REWARD_INVALIDATED_MESSAGE,
    };
  }
  return { action: 'allow', message: null };
}

/** Re-quote après confirmation : faut-il invalider ? */
export function shouldInvalidateConfirmedAfterQuoteResult(quote, err = null) {
  if (err) return true;
  if (!quote || quote.applicable !== true) return true;
  return false;
}

/** Codes catalogue → message UI (pas de soft-skip) */
export function isRewardCatalogueVenteError(err) {
  const code = err?.code || err?.data?.code || '';
  const catalog = [
    'SOLDE_INSUFFISANT',
    'REWARD_NOT_ACTIVE',
    'REWARD_NOT_FOUND',
    'REWARD_PLANIFIEE',
    'REWARD_EXPIREE',
    'PROGRAMME_INACTIF',
    'RIEN_A_REDUIRE',
    'GUEST_REWARD_FORBIDDEN',
    'REWARD_REQUIRES_CLIENT',
    'CROSS_TENANT',
    'AVANTAGES_REDEEM_DISABLED',
    'AVANTAGES_SOLDE_CONCURRENT',
    'QUOTE_NOT_APPLICABLE',
    'CONFIG_INCOMPLETE',
    'ELIGIBILITY_UNSUPPORTED',
    'EFFECT_UNSUPPORTED',
  ];
  return catalog.includes(code) || String(code).startsWith('REWARD_');
}
